import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import { ArrowLeft, ArrowLeftRight, CheckCircle2, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const splitItems = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);
const moneyLine = (t, ag) => {
  const m = ag.money || 0, d = ag.moneyDirection || 'none';
  if (!m || d === 'none') return t('swap.moneyNone', 'None');
  if (d === 'b_to_a') return t('swap.moneyBtoA', 'Offerer pays owner €{n}').replace('{n}', m.toFixed(2));
  return t('swap.moneyAtoB', 'Owner pays offerer €{n}').replace('{n}', m.toFixed(2));
};

// Clear, exact "what is being exchanged" panel.
const TermsCard = ({ ag, t }) => (
  <div className="bg-white rounded-xl border border-teal-200 p-4" data-testid="swap-terms">
    <p className="text-sm"><span className="font-semibold text-gray-800">{t('swap.ownerProvides', 'Owner provides')}:</span> <span data-testid="terms-a">{(ag.aProvides || []).join(', ') || '—'}</span></p>
    <p className="text-sm mt-1"><span className="font-semibold text-gray-800">{t('swap.offererProvides', 'Offerer provides')}:</span> <span data-testid="terms-b">{(ag.bProvides || []).join(', ') || '—'}</span></p>
    <p className="text-sm mt-1"><span className="font-semibold text-gray-800">{t('swap.money', 'Additional money')}:</span> <span data-testid="terms-money">{moneyLine(t, ag)}</span></p>
    {ag.note && <p className="text-xs text-gray-500 mt-2 italic">“{ag.note}”</p>}
  </div>
);

export default function SwapThread() {
  const { offerId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showCounter, setShowCounter] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/swap-offers/${offerId}?token=${token}`);
      setOffer(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || t('swap.loadFail', 'Could not load this swap.'));
    } finally { setLoading(false); }
  }, [offerId, token, t]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/api/swap-offers/${offerId}/${path}?token=${token}&lang=${i18n.language}`, body || {});
      await load();
      setShowCounter(false);
    } catch (e) {
      setErr(e.response?.data?.detail || t('swap.actionFail', 'Action failed.'));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">{t('loading', 'Loading…')}</div>;
  if (!offer) return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      <p className="text-red-600 text-sm" data-testid="swap-load-error">{err}</p>
      <button onClick={() => navigate('/classifieds')} className="mt-4 text-emerald-700 underline text-sm">{t('swap.backToMarket', 'Back to Marketplace')}</button>
    </div>
  );

  const current = (offer.thread || [])[offer.thread.length - 1] || {};
  const role = offer.role;
  const isOpen = offer.status === 'open';
  const isAgreed = offer.status === 'agreed';

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <button onClick={() => navigate('/classifieds')} className="text-gray-600" data-testid="swap-back"><ArrowLeft className="h-5 w-5" /></button>
          <ArrowLeftRight className="h-5 w-5 text-teal-600" />
          <h1 className="text-base font-bold text-gray-900 truncate" data-testid="swap-title">{t('swap.negotiation', 'Swap negotiation')}: {offer.listingTitle}</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        <p className="text-xs text-gray-500" data-testid="swap-role">{t('swap.youAre', 'You are the')} <strong>{role === 'owner' ? t('swap.owner', 'listing owner') : t('swap.offerer', 'person making the offer')}</strong>.</p>

        {isAgreed && (
          <div className="bg-teal-600 text-white rounded-xl p-4 text-center" data-testid="swap-agreed-banner">
            <CheckCircle2 className="h-7 w-7 mx-auto mb-1" />
            <p className="text-lg font-black">✅ {t('swap.agreed', 'SWAP AGREED')}</p>
            <p className="text-xs text-white/85 mt-1">{t('swap.agreedAt', 'Agreed at')} {offer.agreedAt}</p>
          </div>
        )}
        {['declined', 'withdrawn', 'cancelled'].includes(offer.status) && (
          <div className="bg-gray-200 text-gray-700 rounded-xl p-3 text-center text-sm font-semibold uppercase" data-testid="swap-ended-banner">{offer.status}</div>
        )}

        {/* Current EXACT terms */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-1">{isAgreed ? t('swap.finalTerms', 'Final agreed terms') : t('swap.currentTerms', 'Current terms on the table')}</p>
          <TermsCard ag={isAgreed ? (offer.agreedSnapshot || current) : current} t={t} />
        </div>

        {/* Confirmation state */}
        {isOpen && (
          <div className="grid grid-cols-2 gap-2 text-center text-xs">
            <div className={`rounded-lg p-2 ${offer[role === 'owner' ? 'youConfirmed' : 'theyConfirmed'] ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`} data-testid="owner-confirm-state">
              {t('swap.ownerLabel', 'Owner')}: {(role === 'owner' ? offer.youConfirmed : offer.theyConfirmed) ? t('swap.confirmed', 'confirmed ✓') : t('swap.notConfirmed', 'not yet')}
            </div>
            <div className={`rounded-lg p-2 ${offer[role === 'offerer' ? 'youConfirmed' : 'theyConfirmed'] ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`} data-testid="offerer-confirm-state">
              {t('swap.offererLabel', 'Offerer')}: {(role === 'offerer' ? offer.youConfirmed : offer.theyConfirmed) ? t('swap.confirmed', 'confirmed ✓') : t('swap.notConfirmed', 'not yet')}
            </div>
          </div>
        )}

        {err && <p className="text-sm text-red-600" data-testid="swap-error">{err}</p>}

        {/* Actions */}
        {isOpen && !showCounter && (
          <div className="space-y-2" data-testid="swap-actions">
            <button disabled={busy || offer.youConfirmed} onClick={() => act('confirm')}
              className="w-full py-3 bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50" data-testid="swap-confirm-btn">
              {offer.youConfirmed ? t('swap.youConfirmed', 'You have confirmed — waiting for the other person') : t('swap.iAgree', 'I agree to this swap')}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button disabled={busy} onClick={() => setShowCounter(true)} className="py-2.5 bg-amber-100 text-amber-800 rounded-lg font-semibold text-sm border border-amber-200" data-testid="swap-counter-btn">{t('swap.counter', 'Counter offer')}</button>
              <button disabled={busy} onClick={() => act('decline')} className="py-2.5 bg-red-100 text-red-700 rounded-lg font-semibold text-sm border border-red-200" data-testid="swap-decline-btn">{t('swap.decline', 'Decline')}</button>
            </div>
            {role === 'offerer' && <button disabled={busy} onClick={() => act('withdraw')} className="w-full py-2 text-gray-500 text-sm underline" data-testid="swap-withdraw-btn">{t('swap.withdraw', 'Withdraw my offer')}</button>}
            {role === 'owner' && <button disabled={busy} onClick={() => act('cancel')} className="w-full py-2 text-gray-500 text-sm underline" data-testid="swap-cancel-btn">{t('swap.cancel', 'Cancel this negotiation')}</button>}
          </div>
        )}

        {/* Counter form */}
        {isOpen && showCounter && (
          <CounterForm current={current} t={t} busy={busy}
            onCancel={() => setShowCounter(false)}
            onSubmit={(body) => act('propose', body)} />
        )}

        {/* History */}
        {(offer.thread || []).length > 1 && (
          <details className="text-sm" data-testid="swap-history">
            <summary className="cursor-pointer font-semibold text-gray-600">{t('swap.history', 'Offer history')} ({offer.thread.length})</summary>
            <div className="mt-2 space-y-2">
              {offer.thread.map((p, i) => (
                <div key={i} className="rounded-lg bg-white border border-gray-100 p-2 text-xs text-gray-600">
                  <span className="font-semibold">v{p.version} · {p.by === 'owner' ? t('swap.ownerLabel', 'Owner') : t('swap.offererLabel', 'Offerer')}</span>
                  {' — '}{(p.bProvides || []).join(', ')} {p.money ? `+ €${p.money}` : ''} ↔ {(p.aProvides || []).join(', ')}
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Disclaimer */}
        <div className="bg-gray-100 rounded-xl p-3 flex items-start gap-2" data-testid="swap-disclaimer">
          <Info className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
          <p className="text-xs text-gray-500">{offer.disclaimer}</p>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
}

const CounterForm = ({ current, t, busy, onCancel, onSubmit }) => {
  const [a, setA] = useState((current.aProvides || []).join(', '));
  const [b, setB] = useState((current.bProvides || []).join(', '));
  const [money, setMoney] = useState(current.money ? String(current.money) : '');
  const [dir, setDir] = useState(current.moneyDirection || 'none');
  const [note, setNote] = useState('');
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="swap-counter-form">
      <p className="text-sm font-semibold text-amber-900">{t('swap.makeCounter', 'Make a counter offer')}</p>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('swap.ownerProvides', 'Owner provides')} ({t('swap.commaSep', 'comma separated')})</label>
        <input value={a} onChange={e => setA(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="counter-a-input" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('swap.offererProvides', 'Offerer provides')} ({t('swap.commaSep', 'comma separated')})</label>
        <input value={b} onChange={e => setB(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="counter-b-input" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('swap.moneyAmount', 'Money (€)')}</label>
          <input type="number" min="0" step="0.01" value={money} onChange={e => setMoney(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="counter-money-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('swap.moneyDir', 'Money direction')}</label>
          <select value={dir} onChange={e => setDir(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" data-testid="counter-dir-select">
            <option value="none">{t('swap.dirNone', 'No money')}</option>
            <option value="b_to_a">{t('swap.dirBtoA', 'Offerer pays owner')}</option>
            <option value="a_to_b">{t('swap.dirAtoB', 'Owner pays offerer')}</option>
          </select>
        </div>
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} placeholder={t('swap.notePlaceholder', 'Optional note…')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="counter-note-input" />
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => onSubmit({ aProvides: splitItems(a), bProvides: splitItems(b), money: money ? parseFloat(money) : 0, moneyDirection: money && parseFloat(money) > 0 ? dir : 'none', note })}
          className="flex-1 py-2.5 bg-amber-600 text-white rounded-lg font-semibold text-sm" data-testid="counter-send-btn">{t('swap.sendCounter', 'Send counter offer')}</button>
        <button disabled={busy} onClick={onCancel} className="flex-1 py-2.5 bg-white text-gray-700 rounded-lg font-medium text-sm border" data-testid="counter-cancel-btn">{t('cancel', 'Cancel')}</button>
      </div>
    </div>
  );
};
